import React, { useState } from "react";
import styled from "styled-components";

const NodeWrapper = styled.li`
	padding: 0;
	margin: 0;

	a.tree-node-link {
		z-index: 100000;
	}

	ul,
	li,
	button {
		margin: 0;
		padding: 0;
	}

	button i {
		transition: transform 100ms ease;
		margin: 0;
	}

	button.node-icon-rotate > i {
		transform: rotate(180deg);
		transform-origin: center;
	}

	button:focus {
		border: 1px solid #fff;
		text-decoration: none;
	}

	a:focus {
		border: 1px solid #fff;
		outline: none;
	}
`;

const RenderNode = ({
	hasChildren,
	isExpanded,
	skipChildren,
	rootNode,
	setIsExpanded,
	onNodeClick,
	nodeDepth,
}) => {
	const ariaExpanded = hasChildren ? { "aria-expanded": isExpanded } : {};
	const iconName = hasChildren ? "ChevronUpSmall" : "Link";

	return (
		<div className="d-flex flex-row justify-items-center">
			<button
				style={{ width: `${1 + nodeDepth}.8em` }}
				aria-label="show child list"
				aria-hidden={!hasChildren}
				disabled={!hasChildren}
				className={`btn btn-link text-light text-right p-0 px-2 mx-0 mb-auto${
					hasChildren && !isExpanded ? " node-icon-rotate" : ""
				}`}
				onClick={() => setIsExpanded(isExpanded => !isExpanded)}
				tabIndex={!hasChildren || skipChildren ? -1 : null}
			>
				<i
					aria-hidden="true"
					style={{ fontSize: ".7em" }}
					className={`ms-Icon ms-Icon--${iconName}`}
				></i>
			</button>
			<a
				role="treeitem"
				{...ariaExpanded}
				tabIndex={skipChildren ? -1 : null}
				onClick={e => onNodeClick(rootNode, e)}
				className="text-light text-wrap tree-node-link"
				href={`#${rootNode.id}`}
			>
				{rootNode.text}
			</a>
		</div>
	);
};

const Tree = ({ rootNode, skipChildren, onNodeClick, nodeDepth }) => {
	const [isExpanded, setIsExpanded] = useState(
		!skipChildren && rootNode.expanded
	);
	const hasChildren = rootNode.childNodes && rootNode.childNodes.length > 0;
	nodeDepth = nodeDepth || 0;

	const childListStyle = {
		height: isExpanded && rootNode.childNodes ? null : "0px",
		overflow: "hidden",
	};

	return (
		<NodeWrapper role="none">
			<RenderNode
				hasChildren={hasChildren}
				skipChildren={skipChildren}
				isExpanded={isExpanded}
				rootNode={rootNode}
				setIsExpanded={val => setIsExpanded(val)}
				onNodeClick={onNodeClick}
				nodeDepth={nodeDepth}
			/>
			{hasChildren ? (
				<ul
					role="group"
					className="list-unstyled p-0 m-0"
					style={childListStyle}
				>
					{rootNode.childNodes.map(node => (
						<RecursivelyRenderTreeNode
							key={node.id}
							node={node}
							skipChildren={!isExpanded}
							onNodeClick={onNodeClick}
							nodeDepth={nodeDepth + 1}
						/>
					))}
				</ul>
			) : null}
		</NodeWrapper>
	);
};

const RecursivelyRenderTreeNode = ({
	node,
	skipChildren,
	onNodeClick,
	nodeDepth,
}) => (
	<Tree
		key={`${node.id}`}
		rootNode={node}
		skipChildren={skipChildren}
		onNodeClick={onNodeClick}
		nodeDepth={nodeDepth}
	/>
);

const RenderTreeView = ({ data, treeLabelId, onNodeClick }) => {
	return (
		<>
			{data.map ? (
				<div className="text-light py-4">
					<ul
						className="list-unstyled"
						role="tree"
						aria-labelledby={treeLabelId}
					>
						{data.map(node =>
							node.id !== treeLabelId ? (
								<RecursivelyRenderTreeNode
									treeLabelId={treeLabelId}
									key={node.id}
									node={node}
									skipChildren={false}
									onNodeClick={onNodeClick}
								/>
							) : null
						)}
					</ul>
				</div>
			) : null}
		</>
	);
};

export default RenderTreeView;
